/**
 * UIContext — Contexto para preferências de UI do usuário
 *
 * Gerencia o modo Explicativo/Compacto dos dashboards.
 * A preferência é persistida no localStorage para sobreviver a recarregamentos.
 *
 * Modo Explicativo: exibe tooltips expandidos, descrições adicionais e dicas contextuais
 * Modo Compacto: exibe apenas os dados essenciais, sem textos explicativos extras
 */
import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type DashboardMode = "explicativo" | "compacto";

interface UIContextValue {
  /** Modo atual do dashboard */
  dashboardMode: DashboardMode;
  /** Alterna entre modo explicativo e compacto */
  toggleDashboardMode: () => void;
  /** Retorna true se estiver no modo explicativo */
  isExplicativo: boolean;
}

const UIContext = createContext<UIContextValue | undefined>(undefined);

const STORAGE_KEY = "insightnr1_dashboard_mode";

export function UIProvider({ children }: { children: ReactNode }) {
  const [dashboardMode, setDashboardMode] = useState<DashboardMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "compacto" || stored === "explicativo") return stored;
    } catch {
      // localStorage pode não estar disponível em alguns ambientes
    }
    return "explicativo"; // padrão: modo explicativo para novos usuários
  });

  // Persiste a preferência no localStorage quando muda
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, dashboardMode);
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }, [dashboardMode]);

  const toggleDashboardMode = () => {
    setDashboardMode((prev) => (prev === "explicativo" ? "compacto" : "explicativo"));
  };

  return (
    <UIContext.Provider
      value={{
        dashboardMode,
        toggleDashboardMode,
        isExplicativo: dashboardMode === "explicativo",
      }}
    >
      {children}
    </UIContext.Provider>
  );
}

/**
 * Hook para acessar o contexto de UI.
 * Deve ser usado dentro de um componente filho de UIProvider.
 */
export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return ctx;
}
